import { useSiteStore } from '@/store/SiteStoreProvider';
import { useTranslation } from '@whoop/i18n/lang/client';
import React from 'react';
import { Button } from 'ui';
import { useShallow } from 'zustand/react/shallow';
import { getLandingPageSecondaryCTAButtonTextKey } from '../../LandingPageContent/landingPageContentUtils';
import { formatDisplayPrice as formatDisplayPriceWithSiteValues } from 'ui/utils/formatHelpers';
import { CTACard } from 'ui/components/CTACard/CTACard';

interface ICTASectionProps {
  isDisabled: boolean;
  isLoading: boolean;
  skipMemSelection: string | undefined;
  checkSkipMembership: () => Promise<void>;
  handleJoinNow: (destination: string, continueWithoutBundle?: boolean) => void;
  skipGiftSelection: boolean;
  displayFamilyMembershipCTA: boolean;
  onFamilyCTACardClick: () => void;
  familyDisplayPrice: number | null;
  handleGift: () => void;
}

const CTASection: React.FC<ICTASectionProps> = ({
  isDisabled,
  isLoading,
  checkSkipMembership,
  skipMemSelection,
  skipGiftSelection,
  displayFamilyMembershipCTA,
  onFamilyCTACardClick,
  familyDisplayPrice,
  handleGift,
}) => {
  const { currency, region, isRafGiftFlow, language } = useSiteStore(
    useShallow((state) => ({
      language: state.language,
      currency: state.currency,
      region: state.region,
      isRafGiftFlow: state.isRafGiftFlow,
    })),
  );
  const { t } = useTranslation('joinLandingPage', language);

  return (
    <div className='space-y-3 py-3'>
      {isRafGiftFlow ? null : (
        <div className='w-full'>
          <Button
            data-testid='join-now'
            disabled={isLoading || isDisabled}
            onClick={checkSkipMembership}
            size='medium'
            variant='secondary'
          >
            {t('continue')}
          </Button>
        </div>
      )}
      {!skipMemSelection && !skipGiftSelection ? (
        <Button
          className={`w-full ${isRafGiftFlow ? '' : 'bg-transparent'}`}
          data-testid='buy-as-gift'
          disabled={isLoading || isDisabled}
          onClick={handleGift}
          size='medium'
          variant={isRafGiftFlow ? 'bright' : 'primary'}
        >
          {t(
            getLandingPageSecondaryCTAButtonTextKey({
              skipGiftSelection,
            }),
          )}
        </Button>
      ) : null}
      {/* Family CTA Card */}
      {displayFamilyMembershipCTA ? (
        <div className='m-auto'>
          <CTACard
            CTATestId='fp-landing-page-card'
            iconSrc='/community-icon.svg'
            onClick={onFamilyCTACardClick}
            subtitles={[
              t('familyPerPerson', {
                lowestFamilyPrice: `${formatDisplayPriceWithSiteValues(
                  familyDisplayPrice || 0,
                  { currency, language, region },
                  { roundUp: true },
                )}`,
              }),
              t('familySaveBig'),
            ]}
            title={t('familyTitle')}
          />
        </div>
      ) : null}
    </div>
  );
};

export default CTASection;
